/* eslint-disable react-hooks/exhaustive-deps */
import {useMutation, useQuery} from "@tanstack/react-query"
import {KlassappTableHOC} from "HOC"
import {academicTrackPhasesAndShiftsTemplatesService, shiftsService} from "services"
import {KlassappTableProps} from "types/common"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import {useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import {BaseButton, SecondaryButton} from "components"
import styles from "./ShiftTable.module.css"
import {toastError} from "helpers"
import {cloneDeep} from "lodash"
import {Filter} from "../Shifts/Filter"
import {FilterKey} from "types/filter"
import {useFilterStorage} from "hooks"
import {useDebounce} from "react-use"

const defaultFilter = {
    programVersions: [],
    campuses: [],
    programs: []
}

const ShiftTable = (
    props: KlassappTableProps & {
        onClose: () => void
        onRefresh: () => void
        isVisible: boolean
        academicTrackId: number
        academicTrackPhaseShiftTemplateId: number
    }
) => {
    const {
        page,
        total,
        pageSize,
        columns,
        allFields,
        fields,
        onChangePage,
        onChangeRowPerPage,
        onClose,
        isVisible,
        onRefresh,
        dispatch,
        academicTrackPhaseShiftTemplateId,
        dispatchFunc,
        data,
        onChangeFields,
        onChangeAllFields,
        onDraggableColumn,
        onClickSortColumn,
        getCurrentData,
        onUpdateTableData
    } = props

    const {t} = useTranslation(["academics", "common"])
    const {searchValue, currentFilter, setCurrentFilter, changeSearch, clearFilter, applyFilter} = useFilterStorage(
        FilterKey.SelectShiftFilterTrack,
        defaultFilter
    )

    const [searchDebounce, setSearchDebounce] = useState(searchValue)

    useDebounce(() => setSearchDebounce(searchValue), 500, [searchValue])

    const onChangeFilter = (key, value) => {
        const newFilter = {...currentFilter}
        newFilter[key] = value
        setCurrentFilter(newFilter)
    }

    const getFilterParams = () => {
        const filter = {
            search: searchDebounce,
            programIds: currentFilter.programs?.length ? currentFilter.programs.map((program) => program.id) : [],
            programVersionIds: currentFilter.programVersions?.length
                ? currentFilter.programVersions.map((version) => version.id)
                : [],
            campusIds: currentFilter.campuses?.length ? currentFilter.campuses.map((campus) => campus.id) : []
        }

        return filter
    }

    const getShiftsQuery = useQuery(
        ["get-shifts", page, pageSize, searchDebounce],
        async () => {
            const filter = getFilterParams()
            const {data, total} = await shiftsService.getAll({
                linkedObject: true,
                filter
            })
            const newData = data
                .filter((item) => item.isActive)
                .map((item) => {
                    item.id = item.shiftId
                    item.nameHtml = item.name
                    item.isCheckboxDisabled = true
                    item.campusesHtml = (item.campuses ?? []).map((item) => item.name).join(",")
                    item.programsHtml = (item.programs ?? []).map((item) => item.name).join(",")
                    item.programVersionsHtml = (item.programVersions ?? []).map((item) => item.version_name).join(",")
                    return item
                })
            return {data: newData, total}
        },
        {
            enabled: !!isVisible,
            onSuccess: (result) => {
                dispatch({data: result.data, total: result.total})
            },
            initialData: {data: [], total: 0}
        }
    )

    const onApplyFilter = () => {
        applyFilter()
        getShiftsQuery.refetch()
    }

    const addShiftMutation = useMutation(academicTrackPhasesAndShiftsTemplatesService.addShiftTemplate, {
        onSuccess: () => {
            onRefresh()
            onClose()
        },
        onError: () => {
            toastError("Failed to add shift")
        }
    })

    const getColumns = () => {
        return [
            {
                title: t("settings.name"),
                field: "nameHtml"
            },
            {
                title: "Campuses",
                field: "campusesHtml"
            },
            {
                title: "Programs",
                field: "programsHtml"
            },
            {
                title: "Program versions",
                field: "programVersionsHtml"
            }
        ]
    }

    const getPageTitle = () => {
        return t("settings.phases")
    }

    const getFields = () => {
        return [t("settings.name"), "Campuses", "Programs", "Program versions"]
    }

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: getPageTitle},
            {key: "getListData", func: getShiftsQuery.refetch},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
    }, [])

    const onUpdateRowData = (updatedData) => {
        const data = getCurrentData()
        const newData = cloneDeep(data).map((item) => {
            if (item.id === updatedData.id) {
                return {
                    ...item,
                    isChecked: updatedData.isChecked,
                    isCheckboxDisabled: !updatedData.isChecked
                }
            }
            return {
                ...item,
                isChecked: false,
                isCheckboxDisabled: true
            }
        })
        dispatch({data: newData})
    }

    const onClickRowItem = (updatedData) => {
        const data = getCurrentData()
        const newData = cloneDeep(data).map((item) => {
            if (item.id === updatedData.id) {
                return {
                    ...item,
                    isChecked: !updatedData.isChecked,
                    isCheckboxDisabled: updatedData.isChecked
                }
            }
            return {
                ...item,
                isChecked: false,
                isCheckboxDisabled: true
            }
        })
        dispatch({data: newData})
    }

    const onAddShift = () => {
        const data = getCurrentData()
        const selectedShiftId = data.find((item) => item.isChecked).shiftId
        addShiftMutation.mutate({shiftIds: [selectedShiftId], academicTrackPhaseShiftTemplateId})
    }

    return (
        <div style={{paddingTop: "40px"}}>
            <Filter
                searchValue={searchValue}
                changeSearch={changeSearch}
                onApplyFilter={onApplyFilter}
                clearFilter={clearFilter}
                currentFilter={currentFilter}
                onChangeFilter={onChangeFilter}
                setCurrentFilter={setCurrentFilter}
            />

            <KlassappTableHeader
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={onChangePage}
                onChangeRowPerPage={onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={onChangeFields}
                onChangeAllFields={onChangeAllFields}
                onDraggableColumn={onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                data={data}
                menuActions={[]}
                isLoading={getShiftsQuery.isFetching}
                fields={fields}
                allFields={allFields}
                isShowCheckedColumn
                onClickRowItem={onClickRowItem}
                isShowCheckedColumnHeader={false}
                onChangeFields={onChangeFields}
                onUpdateRowData={onUpdateRowData}
                onUpdateTableData={onUpdateTableData}
                onClickSortColumn={onClickSortColumn}
                onDraggableColumn={onDraggableColumn}
                onChangeAllFields={onChangeAllFields}
            />

            <div className={styles.actionDetailWrapPopup}>
                <div>
                    <SecondaryButton onClick={onClose} title={t("common:action.cancelModal")} />
                </div>
                <div className={styles.buttonPopupConfirm}>
                    <BaseButton
                        disabled={!!!data.find((item) => item.isChecked)}
                        onClick={onAddShift}
                        title={t("common:action.confirm")}
                        loading={addShiftMutation.isLoading}
                    />
                </div>
            </div>
        </div>
    )
}

export default KlassappTableHOC(ShiftTable)
